import { Link } from 'react-router-dom';
import { CreditCard } from '@phosphor-icons/react';

import { MoneyDisplay } from '../common/MoneyDisplay';
import { paths } from '../../routes/paths';
import pt from '../../locales/pt';
import { sanitizeProgressPct } from '../../lib/debtDisplayUtils';
import { effectiveMonthlyPayment } from '../../utils/debtPlanUtils';
import { cn } from '../../utils/cn';

function rateLabel(liability) {
  const rate = liability.taeg ?? liability.annual_interest_rate ?? liability.interest_rate;
  if (rate == null || rate === '') return null;
  const n = Number(rate);
  if (Number.isNaN(n)) return null;
  return `${n}% ${pt.liabilities.interestLabel}`;
}

export function LiabilityRow({ liability, onEdit, isPriority = false }) {
  const progress = sanitizeProgressPct(liability.progress_pct);
  const monthly = effectiveMonthlyPayment(liability);
  const rate = rateLabel(liability);
  const balance = Number(liability.current_balance || 0);

  return (
    <article
      className={cn(
        'flex gap-3 rounded-2xl border border-border bg-card p-4',
        isPriority && 'border-sage-400/60 dark:border-sage-600',
      )}
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sage-50 text-sage-700 dark:bg-sage-900/40 dark:text-sage-300">
        <CreditCard className="h-4 w-4" aria-hidden />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-foreground">{liability.name}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {monthly > 0 ? (
            <>
              <MoneyDisplay value={monthly} size="sm" />
              {pt.liabilities.perMonth}
            </>
          ) : null}
          {monthly > 0 && rate ? ' · ' : null}
          {rate}
        </p>

        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-sage-600 dark:bg-sage-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {isPriority ? (
          <span className="mt-2 inline-flex rounded-full bg-sage-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sage-800 dark:bg-sage-900/50 dark:text-sage-300">
            {pt.liabilities.attackFirst}
          </span>
        ) : null}

        <div className="mt-2 flex flex-wrap gap-2">
          <Link
            to={paths.dividasSimulator(liability.id)}
            className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted/80"
          >
            {pt.liabilities.actions.simulate}
          </Link>
          <button
            type="button"
            onClick={() => onEdit?.(liability)}
            className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted/80"
          >
            {pt.liabilities.actions.edit}
          </button>
        </div>
      </div>

      <div className="shrink-0 text-right">
        <p className="font-display text-base font-semibold tabular-nums text-sage-700 dark:text-sage-300">
          {progress}%
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          <MoneyDisplay value={balance} size="sm" /> {pt.liabilities.toPay}
        </p>
      </div>
    </article>
  );
}

export default LiabilityRow;
